import { PageHeader, DataTable, LoadingState, ActionToolbar, EmptyState } from '../components/AdminPageUI';
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import {
  Typography, Box, Button, Grid, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody,
  Card, CardContent, Avatar, Stack, Tooltip, Container,
  Chip, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, FormControl,
  InputLabel, Select, Alert, useMediaQuery
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SchoolIcon from '@mui/icons-material/School';
import ClassIcon from '@mui/icons-material/Class';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadIcon from '@mui/icons-material/Upload';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../utils/Api';

// 🎯 MEMOIZED COMPONENTS
const MemoizedChip = memo(({ label, color, variant = 'filled' }) => (
  <Chip label={label} color={color} variant={variant} size="small" />
));

const MemoizedAvatar = memo(({ icon: Icon, size = 'medium', color = 'primary' }) => (
  <Avatar sx={{
    bgcolor: theme => theme.palette[color].main,
    width: size === 'small' ? 32 : size === 'large' ? 56 : 40,
    height: size === 'small' ? 32 : size === 'large' ? 56 : 40
  }}>
    <Icon fontSize={size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium'} />
  </Avatar>
));

const MemoizedIconButton = memo(({
  onClick,
  icon: Icon,
  color = 'primary',
  disabled = false,
  tooltip = ''
}) => {
  const button = (
    <IconButton
      onClick={onClick}
      color={color}
      disabled={disabled}
      sx={{
        minWidth: 32,
        minHeight: 32,
        borderRadius: 1,
        '&:hover': {
          backgroundColor: theme => alpha(theme.palette[color].main, 0.1),
        },
      }}
    >
      <Icon fontSize="small" />
    </IconButton>
  );

  return (
    <Tooltip title={tooltip}>
      {disabled ? (
        <span style={{ display: 'inline-flex' }}>
          {button}
        </span>
      ) : (
        button
      )}
    </Tooltip>
  );
});

// 🎯 MEMOIZED MOBILE CARD COMPONENT
const MobileClassCard = memo(({
  cls,
  onDownloadSba,
  onDownloadReport,
  onUploadReport,
  loading
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sbaStatus = useMemo(() => ({
    status: 'Available',
    color: 'success'
  }), []);

  const reportStatus = useMemo(() => ({
    status: cls.reportSheetPdfUrl ? 'Available' : 'Not Uploaded',
    color: cls.reportSheetPdfUrl ? 'success' : 'default'
  }), [cls.reportSheetPdfUrl]);

  return (
    <Card sx={{
      mb: 2,
      boxShadow: 2,
      border: `1px solid ${theme.palette.divider}`
    }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header with Class Info */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <MemoizedAvatar icon={ClassIcon} size="small" />
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {cls.displayName || cls.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cls.classTeacher?.name || 'No teacher assigned'}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Student Count and Status */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                STUDENTS
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {cls.students?.length || 0} students
              </Typography>
            </Box>
            <Stack spacing={0.5} alignItems="flex-end">
              <MemoizedChip label={`SBA: ${sbaStatus.status}`} color={sbaStatus.color} />
              <MemoizedChip
                label={`Report: ${reportStatus.status}`}
                color={reportStatus.color}
                variant={reportStatus.color === 'default' ? 'outlined' : 'filled'}
              />
            </Stack>
          </Stack>

          {/* Action Buttons */}
          <ActionToolbar direction="row" spacing={1} justifyContent="space-between">
            <Tooltip title="Download SBA Workbook">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => onDownloadSba(cls._id, cls.displayName || cls.name)}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  SBA
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Download Report Card">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => onDownloadReport(cls)}
                  disabled={!cls.reportSheetPdfUrl}
                  sx={{ flex: 1 }}
                >
                  Report
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Upload Report Card">
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => onUploadReport(cls)}
                sx={{ flex: 1 }}
              >
                Upload
              </Button>
            </Tooltip>
          </ActionToolbar>
        </Stack>
      </CardContent>
    </Card>
  );
});

// 🎯 MEMOIZED TABLE ROW COMPONENT
const ClassTableRow = memo(({
  cls,
  onDownloadSba,
  onDownloadReport,
  onUploadReport,
  loading
}) => {
  const sbaStatus = useMemo(() => ({
    status: 'Available',
    color: 'success'
  }), []);

  const reportStatus = useMemo(() => ({
    status: cls.reportSheetPdfUrl ? 'Available' : 'Not Uploaded',
    color: cls.reportSheetPdfUrl ? 'success' : 'default'
  }), [cls.reportSheetPdfUrl]);

  return (
    <TableRow
      key={cls._id}
      sx={{ '&:hover': { bgcolor: theme => alpha(theme.palette.primary.main, 0.02) } }}
    >
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={2}>
          <MemoizedAvatar icon={ClassIcon} />
          <Typography variant="subtitle1" fontWeight="500">{cls.displayName || cls.name}</Typography>
        </Stack>
      </TableCell>

      <TableCell>
        <Typography variant="body2">
          {cls.classTeacher?.name || 'Not assigned'}
        </Typography>
      </TableCell>



      <TableCell>
        <MemoizedChip label={sbaStatus.status} color={sbaStatus.color} />
      </TableCell>

      <TableCell>
        <MemoizedChip
          label={reportStatus.status}
          color={reportStatus.color}
          variant={reportStatus.color === 'default' ? 'outlined' : 'filled'}
        />
      </TableCell>

      <TableCell align="center">
        <Stack direction="row" spacing={1} justifyContent="center">
          <MemoizedIconButton
            onClick={() => onDownloadSba(cls._id, cls.displayName || cls.name)}
            icon={DownloadIcon}
            color="primary"
            disabled={loading}
            tooltip="Download SBA Workbook"
          />

          <MemoizedIconButton
            onClick={() => onDownloadReport(cls)}
            icon={PictureAsPdfIcon}
            color="secondary"
            disabled={!cls.reportSheetPdfUrl}
            tooltip="Download Report Card"
          />

          <MemoizedIconButton
            onClick={() => onUploadReport(cls)}
            icon={UploadIcon}
            color="primary"
            tooltip="Upload Report Card"
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
});

// 🎯 MAIN OPTIMIZED COMPONENT
const AdminSBAPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // 🎯 STATE MANAGEMENT
  const [classes, setClasses] = useState([]);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [academicYear, setAcademicYear] = useState('');
  const userSelectedAcademicYearRef = useRef(false);
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);

  // 🎯 MEMOIZED USER DATA
  const user = useMemo(() => JSON.parse(localStorage.getItem('user')), []);
  const schoolId = useMemo(() => user?.school?.id || user?.school, [user]);

  // 🎯 OPTIMIZED DATA FETCHING
  const fetchTerms = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await api.get('/term/all', {
        params: { school: schoolId }
      });
      const termsData = res.data?.allTerms || [];
      const currentActiveTerm = termsData.find(t => t.isCurrent);

      let targetYear = academicYear;
      if (!academicYear && currentActiveTerm) {
        if (userSelectedAcademicYearRef.current) return;
        targetYear = currentActiveTerm.academicYear;
        setAcademicYear(current => current || targetYear);
      }

      const filteredTerms = targetYear ? termsData.filter(t => t.academicYear === targetYear) : termsData;
      const uniqueTerms = [...new Set(filteredTerms.map(t => t.term))].sort();
      setAvailableTerms(uniqueTerms);

      const targetCurrentTerm = filteredTerms.find(t => t.isCurrent) || filteredTerms[0];
      if (filteredTerms.length > 0 && !term) {
        setTerm(targetCurrentTerm.term);
      } else if (term && filteredTerms.length > 0) {
        if (!uniqueTerms.includes(term)) {
           setTerm(targetCurrentTerm.term);
        }
      }
    } catch (err) {
      console.error('Failed to fetch terms:', err);
    }
  }, [schoolId, academicYear, term]);

  const fetchAcademicYears = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await api.get('/term/years', { params: { school: schoolId } });
      const years = res.data?.academicYears || [];
      if (years.length > 0) {
        const sortedYears = years.sort((a, b) => b.localeCompare(a));
        setAvailableAcademicYears(sortedYears);
        setAcademicYear(current => current || sortedYears[0]);
      }
    } catch (err) {
      console.error('Failed to fetch academic years:', err);
    }
  }, [schoolId]);

  const fetchClasses = useCallback(async (showToast = false) => {
    try {
      setFetching(true);
      const res = await api.get('/classes');
      const classList = Array.isArray(res.data)
        ? res.data
        : res.data.classes
          ? res.data.classes
          : [];
      setClasses(classList);
      if (showToast) {
        toast.success('Classes updated successfully');
      }
    } catch (err) {
      console.error('Failed to load classes:', err);
      if (showToast) {
        toast.error('Failed to load classes');
      }
    } finally {
      setFetching(false);
    }
  }, []);

  // 🎯 OPTIMIZED EFFECTS
  useEffect(() => {
    fetchAcademicYears();
    fetchClasses(false); // Don't show toast on initial load
  }, [fetchAcademicYears, fetchClasses]);

  useEffect(() => {
    if (academicYear) {
      fetchTerms();
    }
  }, [academicYear, fetchTerms]);

  // 🎯 MEMOIZED EVENT HANDLERS
  const handleDownloadClassWorkbook = useCallback(async (classId, className) => {
    try {
      setLoading(true);
      const response = await api.get(`/sba/admin/download/${classId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${className}_FULL_SBA.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${className} SBA workbook`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download workbook');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDownloadReportCard = useCallback(async (classItem) => {
    if (!classItem.reportSheetPdfUrl) {
      toast.warning('No report card available for this class');
      return;
    }
    try {
      window.open(classItem.reportSheetPdfUrl, '_blank');
    } catch (err) {
      console.error('Error opening report card:', err);
      toast.error('Failed to open report card');
    }
  }, []);

  const handleUploadReportCard = useCallback((classItem) => {
    setSelectedClass(classItem);
    setUploadDialogOpen(true);
  }, []);

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      toast.error('Please select a valid PDF file');
    }
  }, []);

  const submitReportCard = useCallback(async () => {
    if (!selectedFile || !selectedClass) {
      toast.error('Please select a file and class');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('reportPdf', selectedFile);
      formData.append('classId', selectedClass._id);
      formData.append('schoolId', schoolId);
      formData.append('termId', term);

      await api.post('/sba/upload-report-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Report card uploaded successfully!');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setSelectedClass(null);
      await fetchClasses(false); // Don't show toast, we already showed one
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload report card');
    } finally {
      setUploading(false);
    }
  }, [selectedFile, selectedClass, schoolId, term, fetchClasses]);

  const closeUploadDialog = useCallback(() => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setSelectedClass(null);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchClasses(true); // Show toast only on manual refresh
  }, [fetchClasses]);

  // 🎯 MEMOIZED COMPUTED VALUES
  const headerContent = useMemo(() => (
    <Box sx={{ mb: isMobile ? 3 : 4 }}>
      <PageHeader title={<>SBA & Report Card Management</>} description={<>Manage class SBA workbooks and report cards for {user?.school?.name || 'your school'}</>}></PageHeader>

      {/* Academic Year + Term Selection - UPDATED TO GRID V2 */}
      <Card sx={{
        mb: isMobile ? 2 : 3,
        boxShadow: isMobile ? 1 : 2
      }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Grid container spacing={isMobile ? 2 : 3} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={academicYear}
                  onChange={(e) => {
                    userSelectedAcademicYearRef.current = true;
                    setAcademicYear(e.target.value);
                  }}
                  label="Academic Year"
                >
                  {availableAcademicYears.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Term</InputLabel>
                <Select
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  label="Term"
                  startAdornment={<CalendarMonthIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  {availableTerms.map((termItem) => (
                    <MenuItem key={termItem} value={termItem}>{termItem}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {academicYear && term
              ? `Managing data for ${term} term, ${academicYear} academic year`
              : 'Select academic year and term to manage SBA and report cards'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  ), [isMobile, user, academicYear, availableAcademicYears, term, availableTerms]);

  const classesTableContent = useMemo(() => (
    <Box sx={{
      width: '100%',
      overflow: 'auto',
      maxHeight: isTablet ? '60vh' : 'none'
    }}>
      <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
        <TableHead sx={{ bgcolor: 'background.default' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>CLASS NAME</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>CLASS TEACHER</TableCell>

            <TableCell sx={{ fontWeight: 'bold' }}>SBA WORKBOOK</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>REPORT CARD</TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ACTIONS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
{classes.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

          {classes.map((cls) => (
            <ClassTableRow
              key={cls._id}
              cls={cls}
              onDownloadSba={handleDownloadClassWorkbook}
              onDownloadReport={handleDownloadReportCard}
              onUploadReport={handleUploadReportCard}
              loading={loading}
            />
          ))}
        </TableBody>
      </Table></DataTable>
    </Box>
  ), [classes, isTablet, handleDownloadClassWorkbook, handleDownloadReportCard, handleUploadReportCard, loading]);

  const mobileClassesContent = useMemo(() => (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        {classes.map((cls) => (
          <MobileClassCard
            key={cls._id}
            cls={cls}
            onDownloadSba={handleDownloadClassWorkbook}
            onDownloadReport={handleDownloadReportCard}
            onUploadReport={handleUploadReportCard}
            loading={loading}
          />
        ))}
      </Stack>
    </Box>
  ), [classes, handleDownloadClassWorkbook, handleDownloadReportCard, handleUploadReportCard, loading]);

  const loadingContent = useMemo(() => (
    <LoadingState />
  ), []);

  const emptyContent = useMemo(() => (
    <Box sx={{ py: 8, textAlign: 'center', px: 2 }}>
      <SchoolIcon sx={{
        fontSize: isMobile ? 48 : 64,
        color: 'text.secondary',
        mb: 2
      }} />
      <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary">
        No classes found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        There are no classes available for your school
      </Typography>
    </Box>
  ), [isMobile]);

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {headerContent}

      {/* Classes List */}
      <Card sx={{ boxShadow: isMobile ? 1 : 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            p: isMobile ? 2 : 3,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                Class Management ({classes.length})
              </Typography>
              <Button
                onClick={handleRefresh}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                disabled={fetching}
                sx={{ mt: isMobile ? 1 : 0 }}
              >
                {fetching ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Stack>
          </Box>

          {fetching ? loadingContent :
            classes.length === 0 ? emptyContent :
              isMobile ? mobileClassesContent : classesTableContent}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={closeUploadDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ py: isMobile ? 2 : 3 }}>
          Upload Report Card - {selectedClass?.displayName || selectedClass?.name}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Alert severity="info">
              Upload a PDF report card for {selectedClass?.displayName || selectedClass?.name}. It will be linked to the {term} term of {academicYear}.
            </Alert>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              style={{
                width: '100%',
                padding: isMobile ? '8px' : '12px',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '4px'
              }}
            />
            {selectedFile && (
              <Alert severity="success">
                Selected: {selectedFile.name}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={closeUploadDialog}
            disabled={uploading}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ mb: isMobile ? 1 : 0 }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitReportCard}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// 🎯 EXPORT MEMOIZED COMPONENT
export default memo(AdminSBAPage);
